import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useTranslation } from "react-i18next";

interface ValidationChange {
  field: string;
  oldValue: string;
  newValue: string;
  type: "price_increase" | "price_decrease" | "other";
}

interface ValidationChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  changes: ValidationChange[];
  onConfirm: () => void;
  onRemove: () => void;
}

export const ValidationChangesDialog = ({
  open,
  onOpenChange,
  changes,
  onConfirm,
  onRemove,
}: ValidationChangesDialogProps) => {
  const { t } = useTranslation();
  const hasPriceIncrease = changes.some((c) => c.type === "price_increase");

  const title = hasPriceIncrease
    ? `⚠️ ${t("cart.validationDialogTitlePriceIncrease")}`
    : `ℹ️ ${t("cart.validationDialogTitleInfoUpdated")}`;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>{t("cart.validationDialogIntro")}</p>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/3">{t("cart.validationDialogField")}</TableHead>
                      <TableHead className="w-1/3">{t("cart.validationDialogOldValue")}</TableHead>
                      <TableHead className="w-1/3">{t("cart.validationDialogNewValue")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {changes.map((change, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{change.field}</TableCell>
                        <TableCell className="text-muted-foreground">{change.oldValue}</TableCell>
                        <TableCell
                          className={
                            change.type === "price_increase"
                              ? "text-destructive font-semibold"
                              : "text-foreground font-medium"
                          }
                        >
                          {change.newValue}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <p className="text-sm">{t("cart.validationDialogQuestion")}</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onRemove}>{t("cart.validationDialogRemove")}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>{t("cart.validationDialogKeep")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

